import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { BaseLinkContextProvider } from "shared/components/common/base-link/context";
import { useTrackImpressionEventByIntersection } from "shared/hooks";

import { PlusArchiveTitle } from "scmp-app/components/plus/archive/archive-title";
import { usePlusArticlePopup } from "scmp-app/components/plus/article-popup/hooks";
import { usePlusDetailedListingPopup } from "scmp-app/components/plus/detailed-listing-popup/hooks";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { RecirculationWidgetImpressionEvent } from "scmp-app/components/tracking/google-analytics-4/types";
import type { dailyPulsePlusArchiveQuery$key } from "scmp-app/queries/__generated__/dailyPulsePlusArchiveQuery.graphql";

import { Container, ContentItemContainer, StyledContentItemPlusSecondary } from "./styles";

type Props = {
  className?: string;
  reference: dailyPulsePlusArchiveQuery$key;
};
export const PlusArchiveDailyPulse: FunctionComponent<Props> = ({ className, reference }) => {
  const content = useFragment(
    graphql`
      fragment dailyPulsePlusArchiveQuery on Query
      @argumentDefinitions(dailyPulseTypeUuid: { type: "String!" }) {
        pulseArchiveDailyPulse: articles(
          filter: { typeIds: [$dailyPulseTypeUuid] }
          first: 4
          orderBy: { field: PUBLISHED_DATE, direction: DESC }
        ) {
          edges {
            node {
              entityId
              ...plusSecondaryContentItemContent
              ...hooksPlusArticleLinkHandlerArticle
            }
          }
        }
      }
    `,
    reference,
  );

  const { handleOpen: handleOpenDetailedListingPopup } = usePlusDetailedListingPopup();
  const { handleOpen: handleOpenArticlePopup } = usePlusArticlePopup();

  const { captureTrackImpressionEventTargetElement } =
    useTrackImpressionEventByIntersection<RecirculationWidgetImpressionEvent>({
      ga4TrackingHandler: sendGA4Tracking,
      getGa4Event: () => ({
        action: "imp",
        category: "recirculation",
        customized_parameters: {
          list_of_articles: (
            content?.pulseArchiveDailyPulse.edges.map(edge => edge.node.entityId) ?? []
          ).join(","),
          page_type: "archive",
          widget_name: "daily_pulse",
        },
        subcategory: "widget",
      }),
      options: {
        isSendGA4Tracking: true,
        shouldSendOnce: true,
      },
    });

  return (
    <BaseLinkContextProvider
      customQueryParameters={{
        module: "daily_pulse",
      }}
    >
      <Container className={className} ref={captureTrackImpressionEventTargetElement}>
        <PlusArchiveTitle
          onClick={() =>
            handleOpenDetailedListingPopup({
              pgtype: "archive",
              type: "daily-pulse",
            })
          }
          title="Daily Pulse"
        />
        <ContentItemContainer>
          {content?.pulseArchiveDailyPulse.edges.map(edge => (
            <StyledContentItemPlusSecondary
              key={edge.node.entityId}
              onClick={() =>
                handleOpenArticlePopup(edge.node, {
                  pageType: "archive",
                  widgetName: "daily_pulse",
                })
              }
              reference={edge.node}
              withArticleType={false}
            />
          ))}
        </ContentItemContainer>
      </Container>
    </BaseLinkContextProvider>
  );
};

PlusArchiveDailyPulse.displayName = "PlusArchiveDailyPulse";
